import type {
  ListMbcAdminAssignmentsResponse,
  MbcAdminGuideline,
} from "@nlc/shared-types";

export type GuidelineScope =
  | {
      designTypeId: number;
      kind: "designType";
      name: string;
      subCategoryId: null | number;
    }
  | { kind: "subCategory"; name: string; subCategoryId: number };

export type AssignmentSource = "default" | "own" | "subCategory";

export interface AssignmentLayer {
  guidelineId: null | string;
  notes: null | string;
  source: AssignmentSource;
}

export interface Effective<T> {
  source: AssignmentSource;
  value: T;
}

export interface EffectiveAssignment {
  guideline: Effective<MbcAdminGuideline> | null;
  notes: Effective<string> | null;
}

export function assignmentLayers(
  assignments: ListMbcAdminAssignmentsResponse,
  scope: GuidelineScope,
): AssignmentLayer[] {
  const layer = (
    source: AssignmentSource,
    assignment?: { guidelineId: null | string; notes: null | string },
  ): AssignmentLayer => ({
    guidelineId: assignment?.guidelineId ?? null,
    notes: assignment?.notes ?? null,
    source,
  });
  const subCategoryAssignment = (subCategoryId: number) =>
    assignments.subCategories.find(({ mysqlId }) => mysqlId === subCategoryId);

  const layers =
    scope.kind === "designType"
      ? [
          layer(
            "own",
            assignments.designTypes.find(
              ({ mysqlId }) => mysqlId === scope.designTypeId,
            ),
          ),
          ...(scope.subCategoryId === null
            ? []
            : [
                layer(
                  "subCategory",
                  subCategoryAssignment(scope.subCategoryId),
                ),
              ]),
        ]
      : [layer("own", subCategoryAssignment(scope.subCategoryId))];

  return [...layers, layer("default", assignments.default)];
}

export function resolveEffectiveAssignment(
  guidelines: MbcAdminGuideline[],
  layers: AssignmentLayer[],
): EffectiveAssignment {
  const setLayer = layers.find(({ guidelineId }) => guidelineId !== null);
  const guideline = guidelines.find(({ _id }) => _id === setLayer?.guidelineId);
  const notesLayer = layers.find(({ notes }) => notes !== null);

  return {
    guideline:
      setLayer && guideline
        ? { source: setLayer.source, value: guideline }
        : null,
    notes: notesLayer?.notes
      ? { source: notesLayer.source, value: notesLayer.notes }
      : null,
  };
}
